import {
  AgencyAgentModelLicenceType,
  AgentDetails,
  AgentDetailsLicenceType,
  CompanyDetails,
  LicenceDetails,
  States,
  UploadedDocument,
  useCreateAgent,
  useGetAgencyMembers,
  useGetTeamsForAgency,
  useInvalidateUseGetAgencyMembers,
  useInvalidateUseGetTeamsForAgency,
  useSendInvitationEmail,
  useSetAgentDetails,
  useSoftDeleteAgent,
} from '@urbanx/agx.agencies.client';
import { AgxToast } from '@urbanx/agx-ui-components';
import { AgentRole } from '@urbanx/agx.auth.client';
import { toDateOnlyString } from '@urbanx/agx.ui-components2';
import {
  AgxButton,
  AgxColumn,
  AgxDatePicker,
  AgxDivider,
  AgxDropdown,
  AgxRow,
  AgxSaveChangesButton,
  AgxSelectChip,
  AgxTextArea,
  AgxTextInput,
  AgxToggleSwitch,
  AgxTypography,
  Icon_CheckmarkCircleOutlineBig,
  Icon_Arrow,
  SAVE_BUTTON_STATES,
  SelectChipOption,
} from '@urbanx/agx.ui-components2';
import { FormPrompt } from 'components/FormPrompt';
import Popup from 'components/Popup/Popup';
import Panel from 'components/panel/Panel';
import { useSelectedAgency } from 'hooks';
import useSelectedAgent from 'hooks/useSelectedAgent';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { cloneDeep, isEqual, set } from 'lodash-es';
import LogoUpload from './LogoUpload';
import { AgencyContext } from 'contexts';
import AgentCrmRegistration from './agent-crm-registration/AgentCrmRegistration';
import DeleteConfirmation from 'components/delete-confirmation/DeleteConfirmation';
import { useNavigate } from 'react-router-dom';
import { LicenceTypeOptions } from './LicenceTypeOptions';
import { DefaultTeamMember } from './DefaultTeamMember';
import LoadingState from 'components/loadingState/LoadingState';
import { NestedKeyOf } from 'utils/NestedKeyOf';
import { isDirtyEqual } from 'utils/compares';
import { AgxToastState } from 'types/commonTypes';
import { isEmailValid, normalizeEmail } from 'helpers/emailHelper';

const enumToOptions = <T extends Record<string, string | number>>(enumObject: T) => {
  return Object.entries(enumObject)
    .filter(([key]) => Number.isNaN(Number(key)))
    .map(([key, value]) => ({
      label: key.replace(/([a-z])([A-Z])/g, '$1 $2'),
      value: String(value),
    }));
};

const AgentRoleOptions = enumToOptions(AgentRole);

interface AddOrEditTeamMemberPageProps {
  isEdit?: boolean;
}

const cloneTeamMemberForm = (agent?: AgentDetails): AgentDetails =>
  cloneDeep(agent ?? DefaultTeamMember);

const SUCCESS_MODAL_AUTO_DISMISS_MS = 1600;

const AddOrEditTeamMemberPage = ({ isEdit = false }: AddOrEditTeamMemberPageProps) => {
  const { agent: selectedAgent, isPending: isSelectedAgentPending } = useSelectedAgent();
  const { agency: selectedAgency, isLoading: isSelectedAgencyPending } = useSelectedAgency();
  const { data: agencyMembers, isPending: isAgencyMembersPending } = useGetAgencyMembers(
    selectedAgency?.id ?? '',
    !!selectedAgency?.id
  );

  const [forceCheck, setForceCheck] = useState(false);

  const [agencyContext] = useContext(AgencyContext);

  const [teamMemberForm, setTeamMemberForm] = useState<AgentDetails>(() =>
    cloneTeamMemberForm(selectedAgent)
  );
  const [initialTeamMemberForm, setInitialTeamMemberForm] = useState<AgentDetails>(() =>
    cloneTeamMemberForm(selectedAgent)
  );
  const [saveButtonState, setSaveButtonState] = useState<SAVE_BUTTON_STATES>(
    SAVE_BUTTON_STATES.UNSAVED
  );
  const [toastState, updateToastState] = useState<AgxToastState>({
    color: 'success',
    message: '',
    open: false,
  });
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [hasCreatedMember, setHasCreatedMember] = useState(false);

  useEffect(() => {
    if (!isEdit) {
      setTeamMemberForm(cloneTeamMemberForm());
      setInitialTeamMemberForm(cloneTeamMemberForm());
      setSaveButtonState(SAVE_BUTTON_STATES.UNSAVED);
      setHasCreatedMember(false);
      return;
    }

    if (!selectedAgent) return;

    setTeamMemberForm(cloneTeamMemberForm(selectedAgent));
    setInitialTeamMemberForm(cloneTeamMemberForm(selectedAgent));
    setSaveButtonState(SAVE_BUTTON_STATES.UNSAVED);
    setHasCreatedMember(false);
  }, [isEdit, selectedAgent?.id]);

  const isFormDirty = useMemo(() => {
    return !isDirtyEqual(teamMemberForm, initialTeamMemberForm);
  }, [teamMemberForm, initialTeamMemberForm]);

  const normalizedEmail = useMemo(
    () => normalizeEmail(teamMemberForm.email),
    [teamMemberForm.email]
  );

  const hasValidEmail = isEmailValid(normalizedEmail);

  const duplicateAgencyEmail = useMemo(() => {
    if (isEdit || hasCreatedMember || !normalizedEmail) return false;

    return (
      agencyMembers?.some(
        (member) => !member.softDeleted && normalizeEmail(member.email) === normalizedEmail
      ) ?? false
    );
  }, [agencyMembers, hasCreatedMember, isEdit, normalizedEmail]);

  const emailErrorText = useMemo(() => {
    if (duplicateAgencyEmail) return 'User already exists';
    if (normalizedEmail && !hasValidEmail) return 'Please specify a valid email address';

    return undefined;
  }, [duplicateAgencyEmail, hasValidEmail, normalizedEmail]);

  const isAddFormValid = useMemo(() => {
    return (
      isEdit ||
      (!isAgencyMembersPending &&
        (teamMemberForm.firstName ?? '').trim().length > 0 &&
        (teamMemberForm.lastName ?? '').trim().length > 0 &&
        hasValidEmail &&
        !duplicateAgencyEmail)
    );
  }, [
    duplicateAgencyEmail,
    hasValidEmail,
    isAgencyMembersPending,
    isEdit,
    teamMemberForm.firstName,
    teamMemberForm.lastName,
  ]);

  const isExistingMember = useMemo(() => {
    return isEdit || hasCreatedMember || !!teamMemberForm.id;
  }, [hasCreatedMember, isEdit, teamMemberForm.id]);

  const onDetailsChangeHandler = useCallback(
    (
      value:
        | string
        | boolean
        | string[]
        | UploadedDocument
        | undefined
        | AgentRole
        | LicenceDetails[]
        | number,
      prop: NestedKeyOf<AgentDetails>
    ) => {
      if (teamMemberForm) {
        const updatedTeamMemberForm = set({ ...teamMemberForm }, prop, value);
        setTeamMemberForm(updatedTeamMemberForm);
      }
    },
    [teamMemberForm, setTeamMemberForm]
  );

  const licenceTypeOptionsForState = useMemo(
    () =>
      LicenceTypeOptions.filter(
        (x) =>
          (x.state && agencyContext?.states?.includes(x.state as unknown as States)) || !x.state
      ).map((x) => x.option),
    [agencyContext?.states]
  );

  const { data: agencyTeams, isPending: isAgencyTeamsPending } = useGetTeamsForAgency(
    selectedAgency?.id ?? '',
    !!selectedAgency?.id
  );

  const allTeamsOptions = useMemo(() => {
    return (
      agencyTeams
        ?.filter((t) => t.id !== undefined && t.name !== undefined)
        .map((t) => ({ value: t.id!, label: t.name! })) ?? []
    );
  }, [agencyTeams]);

  const onTeamSelectionChanged = useCallback((selectedOptions: SelectChipOption[]) => {
    const selectedTeamIds = selectedOptions.map((option) => option.value);
    const nextTeams = selectedTeamIds.length > 0 ? selectedTeamIds : [];

    setTeamMemberForm((prev) => {
      if (isEqual(prev.teams ?? [], nextTeams)) return prev;

      return {
        ...prev,
        teams: nextTeams,
      };
    });
  }, []);

  const canViewLicenseeDetails = useMemo(() => {
    const states = agencyContext?.states;
    const licenceType = teamMemberForm?.licenceType;

    if (!states || states.length === 0 || !licenceType) return false;

    switch (licenceType) {
      case AgentDetailsLicenceType.Class1:
        return states.includes(States.NSW);
      case AgentDetailsLicenceType.RealEstateLicence:
        return states.includes(States.QLD);
      default:
        return false;
    }
  }, [agencyContext?.states, teamMemberForm?.licenceType]);

  const onCompanyDetailsChangeHandler = useCallback(
    (value: string | LicenceDetails[], prop: keyof CompanyDetails) => {
      if (teamMemberForm) {
        const updatedCompanyDetails: CompanyDetails = {
          ...teamMemberForm.agentCompanyDetails,
          [prop]: value,
        };

        setTeamMemberForm({
          ...teamMemberForm,
          agentCompanyDetails: { ...updatedCompanyDetails },
        });
      }
    },
    [teamMemberForm]
  );

  const [licenceDetails, setLicenceDetails] = useState<LicenceDetails>(() =>
    teamMemberForm?.agentCompanyDetails?.licences &&
    teamMemberForm.agentCompanyDetails.licences.length > 0
      ? teamMemberForm.agentCompanyDetails.licences[0]
      : {
          licenceNumber: '',
          expiryDate: undefined,
          state: undefined,
          agreements: [],
        }
  );

  const [emailInviteSent, setEmailInviteSent] = useState<boolean>(
    teamMemberForm?.emailInvitationSent ?? false
  );

  const invalidateAgencyMembers = useInvalidateUseGetAgencyMembers();
  const invalidateTeamsForAgency = useInvalidateUseGetTeamsForAgency();

  const { sendInvitationEmail, sendInvitationEmailPending } = useSendInvitationEmail({
    onSuccess: async () => {
      setEmailInviteSent(true);

      await invalidateAgencyMembers(selectedAgency?.id || '');

      updateToastState({
        color: 'success',
        message: 'Email Sent!',
        open: true,
      });
    },
    onError: () => {
      setEmailInviteSent(false);

      updateToastState({
        color: 'error',
        message: 'Error sending email invitation',
        open: true,
      });
    },
  });

  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);

  const navigate = useNavigate();

  const { softDeleteAgent, softDeleteAgentPending } = useSoftDeleteAgent({
    onSuccess: async () => {
      setShowDeleteConfirmation(false);
      await invalidateAgencyMembers(selectedAgency?.id || '');
      await invalidateTeamsForAgency(selectedAgency?.id || '');
      navigate('../');
    },
    onError: () => {
      updateToastState({
        color: 'error',
        message: 'Error saving changes',
        open: true,
      });
    },
  });

  const { createAgent, createAgentPending } = useCreateAgent({
    onSuccess: async (response, inputData) => {
      setHasCreatedMember(true);
      await invalidateAgencyMembers(selectedAgency?.id || '');
      await invalidateTeamsForAgency(selectedAgency?.id || '');
      const createdTeamMember = cloneTeamMemberForm({
        ...inputData,
        id: response?.agentId || inputData.id,
      });

      setTeamMemberForm(createdTeamMember);
      setInitialTeamMemberForm(createdTeamMember);
      setSaveButtonState(SAVE_BUTTON_STATES.SAVED);
      setSuccessMessage(`${inputData.firstName} ${inputData.lastName} has been added to the team!`);
      setShowSuccessModal(true);
    },
    onError: () => {
      setSaveButtonState(SAVE_BUTTON_STATES.ERROR);
      updateToastState({
        color: 'error',
        message: 'Error saving changes',
        open: true,
      });
    },
  });

  const { setAgentDetails, setAgentDetailsPending } = useSetAgentDetails({
    onSuccess: async (_, inputData) => {
      await invalidateAgencyMembers(selectedAgency?.id || '');
      await invalidateTeamsForAgency(selectedAgency?.id || '');
      setInitialTeamMemberForm(cloneTeamMemberForm(inputData));
      setSaveButtonState(SAVE_BUTTON_STATES.SAVED);
      setSuccessMessage('Changes saved successfully.');
      setShowSuccessModal(true);
    },
    onError: () => {
      setSaveButtonState(SAVE_BUTTON_STATES.ERROR);
      updateToastState({
        color: 'error',
        message: 'Error saving changes',
        open: true,
      });
    },
  });

  const isSavingChanges = createAgentPending || setAgentDetailsPending;
  const isSaveDisabled = useMemo(() => {
    if (isSavingChanges) return true;
    if (!isFormDirty) return true;

    return !isExistingMember && !isAddFormValid;
  }, [isAddFormValid, isExistingMember, isFormDirty, isSavingChanges]);

  const saveChangesState = useMemo(() => {
    if (isSavingChanges) return SAVE_BUTTON_STATES.SAVING;
    if (isFormDirty) return SAVE_BUTTON_STATES.UNSAVED;
    if (saveButtonState === SAVE_BUTTON_STATES.ERROR) return SAVE_BUTTON_STATES.ERROR;
    return saveButtonState;
  }, [isFormDirty, isSavingChanges, saveButtonState]);

  useEffect(() => {
    if (isFormDirty) {
      setSaveButtonState(SAVE_BUTTON_STATES.UNSAVED);
    }
  }, [isFormDirty]);

  const handleSendInvitationEmail = useCallback(() => {
    if (sendInvitationEmailPending || emailInviteSent || isSavingChanges) return;
    sendInvitationEmail({
      agentId: teamMemberForm.id || '',
    });
  }, [
    sendInvitationEmailPending,
    emailInviteSent,
    isSavingChanges,
    sendInvitationEmail,
    teamMemberForm?.id,
  ]);

  const onSaveChangesClicked = useCallback(async () => {
    if (isSavingChanges || !isFormDirty) return;
    if (!isExistingMember && !isAddFormValid) return;

    if (isExistingMember) {
      await setAgentDetails({
        id: teamMemberForm.id || '',
        firstName: teamMemberForm.firstName,
        lastName: teamMemberForm.lastName,
        title: teamMemberForm.title,
        email: teamMemberForm.email,
        phone: teamMemberForm.phone,
        role: teamMemberForm.role,
        teams: teamMemberForm.teams,
        licenceType: teamMemberForm.licenceType,
        useLicenseeDetails: teamMemberForm.useLicenseeDetails,
        instructionsConditions: teamMemberForm.instructionsConditions,
        agentCompanyDetails: teamMemberForm.agentCompanyDetails,
        photo: teamMemberForm.photo,
        agencyId: selectedAgency?.id || '',
        emailInvitationSent: teamMemberForm.emailInvitationSent || false,
        softDeleted: teamMemberForm.softDeleted || false,
      });
    } else {
      await createAgent({
        id: teamMemberForm.email,
        agencyId: selectedAgency?.id || '',
        firstName: teamMemberForm.firstName,
        lastName: teamMemberForm.lastName,
        title: teamMemberForm.title,
        email: teamMemberForm.email,
        phone: teamMemberForm.phone,
        role: teamMemberForm.role,
        teams: teamMemberForm.teams,
        licenceType: teamMemberForm.licenceType,
        useLicenseeDetails: teamMemberForm.useLicenseeDetails,
        instructionsConditions: teamMemberForm.instructionsConditions,
        agentCompanyDetails: teamMemberForm.agentCompanyDetails,
        photo: teamMemberForm.photo,
        emailInvitationSent: false,
        softDeleted: false,
      });
    }
  }, [
    isAddFormValid,
    isExistingMember,
    isSavingChanges,
    isFormDirty,
    teamMemberForm,
    selectedAgency?.id,
    setAgentDetails,
    createAgent,
  ]);

  const onBack = useCallback(() => {
    navigate('../team-members');
  }, [navigate]);

  const onBackClicked = useCallback(() => {
    if (isFormDirty) {
      setForceCheck(true);
      return;
    }
    onBack();
  }, [isFormDirty, onBack]);

  if (
    (isEdit && isSelectedAgentPending) ||
    isSelectedAgencyPending ||
    (isEdit && isAgencyMembersPending) ||
    isAgencyTeamsPending
  ) {
    return <LoadingState />;
  }

  const pageTitle = `${isExistingMember ? 'Edit' : 'Add'} Team Member`;

  const handleSuccessModalClose = () => {
    setShowSuccessModal(false);

    if (isEdit || !teamMemberForm.id) return;

    navigate(`../team-members/edit/${teamMemberForm.id}`, { replace: true });
  };

  return (
    <AgxColumn fill gap="spacing-lg">
      <AgxRow fill justifyContent="space-between" alignItems="alignCenter">
        <AgxButton
          id="return-button"
          variant="naked"
          text={pageTitle}
          leftIcon={<Icon_Arrow direction="left" height={22} width={22} />}
          onClick={onBackClicked}
        />
        <AgxRow gap="spacing-lg" alignItems="alignCenter">
          {isExistingMember && (
            <AgxButton
              id={'deleteTeamMemberButton'}
              text={'Delete team member'}
              variant={'naked'}
              size="medium"
              onClick={() => setShowDeleteConfirmation(true)}
              danger
            />
          )}
          <AgxSaveChangesButton
            id="save-team-member-button"
            text="Save changes"
            variant={'primary'}
            size="medium"
            state={saveChangesState}
            onClick={onSaveChangesClicked}
            disabled={isSaveDisabled}
          />
        </AgxRow>
      </AgxRow>
      <AgxDivider variant={'horizontal'} />

      <FormPrompt
        hasUnsavedChanges={isFormDirty}
        forceCheck={forceCheck}
        resetForceCheck={setForceCheck}
        onBack={onBack}
      />
      <AgxToast selector="#agxToast" toastState={toastState} />

      <AgxRow gap={'spacing-3xl'} fill>
        <Panel title="Team Member Details" halfWidth>
          <>
            <LogoUpload
              onImageChange={(logo) => {
                onDetailsChangeHandler(logo, 'photo');
              }}
              logo={teamMemberForm.photo}
              agencyId={selectedAgency?.id || ''}
              isDisabled={isSavingChanges}
            />
            <AgxRow gap="spacing-3xl" fill>
              <AgxTextInput
                id="txtFirstName"
                type="text"
                label="First name"
                required
                hideOptionalText
                stretch
                defaultValue={teamMemberForm.firstName}
                onInputValueChange={({ value }) => onDetailsChangeHandler(value, 'firstName')}
              />
              <AgxTextInput
                id="txtLastName"
                type="text"
                label="Last name"
                required
                hideOptionalText
                stretch
                defaultValue={teamMemberForm.lastName}
                onInputValueChange={({ value }) => onDetailsChangeHandler(value, 'lastName')}
              />
            </AgxRow>
            <AgxTextInput
              id="txtJobTitle"
              type="text"
              label="Job Title"
              hideOptionalText
              stretch
              defaultValue={teamMemberForm.title}
              onInputValueChange={({ value }) => onDetailsChangeHandler(value, 'title')}
            />
            <AgxTextInput
              id="txtEmail"
              type="email"
              label="Email"
              required
              hideOptionalText
              disabled={isExistingMember}
              stretch
              defaultValue={teamMemberForm.email}
              errorText={emailErrorText}
              onInputValueChange={({ value }) => onDetailsChangeHandler(value, 'email')}
            />
            <AgxRow gap="spacing-2xl" fill>
              <AgxTextInput
                id="txtPhone"
                type="text"
                label="Phone"
                hideOptionalText
                stretch
                defaultValue={teamMemberForm.phone}
                onInputValueChange={({ value }) => onDetailsChangeHandler(value, 'phone')}
              />
              <AgxDropdown
                id="selUserRole"
                options={AgentRoleOptions}
                onChange={(value) => {
                  onDetailsChangeHandler(value as AgencyAgentModelLicenceType, 'role');
                }}
                label="Role"
                selected={teamMemberForm?.role ?? AgentRoleOptions[0].value}
                required
              />
            </AgxRow>
            <AgxSelectChip
              id="selTeam"
              label="Team"
              size="medium"
              type="multi-select"
              variant="chip"
              defaultSelection={teamMemberForm.teams}
              onSelect={onTeamSelectionChanged}
              options={allTeamsOptions}
            />
            <AgxDropdown
              id="selLicenseType"
              label="Real Estate Licence"
              hideOptionalText
              options={licenceTypeOptionsForState}
              selected={teamMemberForm.licenceType as AgentDetailsLicenceType}
              onChange={(value) => {
                onDetailsChangeHandler(value, 'licenceType');
              }}
            />
            {canViewLicenseeDetails && (
              <AgxRow gap="spacing-lg" justifyContent="center">
                <AgxToggleSwitch
                  id="switchLicenseeDetails"
                  checked={teamMemberForm?.useLicenseeDetails ?? false}
                  onChange={(isChecked: boolean) => {
                    onDetailsChangeHandler(isChecked, 'useLicenseeDetails');
                  }}
                />
                <AgxTypography id={'lbllicenseeDetails'} type={'body_ui'} size={'medium'}>
                  Licensee Details
                </AgxTypography>
              </AgxRow>
            )}
            {teamMemberForm.useLicenseeDetails && canViewLicenseeDetails && (
              <>
                <AgxTextInput
                  id="txtLicenseeName"
                  type="text"
                  label="Licensee name"
                  hideOptionalText
                  stretch
                  defaultValue={teamMemberForm.agentCompanyDetails?.licenseeName ?? ''}
                  onInputValueChange={({ value }) =>
                    onCompanyDetailsChangeHandler(value, 'licenseeName')
                  }
                />
                <AgxRow gap="spacing-3xl" fill>
                  <AgxTextInput
                    id="txtAbn"
                    type="abn"
                    label="ABN"
                    hideOptionalText
                    stretch
                    defaultValue={teamMemberForm.agentCompanyDetails?.abn ?? ''}
                    onInputValueChange={({ value }) => onCompanyDetailsChangeHandler(value, 'abn')}
                  />
                  <AgxTextInput
                    id="txtAcn"
                    type="acn"
                    label="ACN"
                    hideOptionalText
                    stretch
                    defaultValue={teamMemberForm.agentCompanyDetails?.acn ?? ''}
                    onInputValueChange={({ value }) => onCompanyDetailsChangeHandler(value, 'acn')}
                  />
                </AgxRow>
                <AgxRow gap="spacing-3xl" fill>
                  <AgxTextInput
                    id="txtlicenceNumber"
                    type="text"
                    label="Licence Number"
                    hideOptionalText
                    stretch
                    defaultValue={licenceDetails.licenceNumber}
                    onInputValueChange={({ value }) => {
                      const updatedLicence: LicenceDetails = {
                        licenceNumber: value,
                        expiryDate: licenceDetails.expiryDate,
                      };
                      setLicenceDetails(updatedLicence);
                      onCompanyDetailsChangeHandler([updatedLicence], 'licences');
                    }}
                  />
                  <AgxDatePicker
                    id="dtExipryDate"
                    variant="compact"
                    label="Expiry Date"
                    required
                    stretch
                    value={licenceDetails.expiryDate}
                    onChange={(value) => {
                      const updatedLicence: LicenceDetails = {
                        licenceNumber: licenceDetails.licenceNumber,
                        expiryDate: toDateOnlyString(value),
                      };
                      setLicenceDetails(updatedLicence);
                      onCompanyDetailsChangeHandler([updatedLicence], 'licences');
                    }}
                  />
                </AgxRow>
              </>
            )}
            <AgxColumn gap="spacing-lg" fill>
              <AgxTypography id="extrasText" type="body_ui" size="small">
                Extras
              </AgxTypography>
              <AgxDivider variant="horizontal" />
            </AgxColumn>

            <AgxTextArea
              id="instructionsConditions"
              label="Reassignment Clause"
              defaultValue={teamMemberForm.instructionsConditions ?? ''}
              onInputValueChange={({ value }: { value: string }) =>
                onDetailsChangeHandler(value, 'instructionsConditions')
              }
              rows={6}
              maxLength={1100}
              hideOptionalText
              stretch
            />
            {isExistingMember && (
              <AgxRow>
                <AgxColumn gap="spacing-lg">
                  <AgxTypography id="emailInvitation" type="body_ui" size="small">
                    Email Invitation
                  </AgxTypography>
                  <AgxButton
                    id="sendInvitationEmailButton"
                    text={
                      sendInvitationEmailPending
                        ? 'Sending...'
                        : emailInviteSent
                          ? 'Resend Invite'
                          : 'Send Invite'
                    }
                    size="medium"
                    variant="primary"
                    disabled={sendInvitationEmailPending || emailInviteSent || isSavingChanges}
                    onClick={handleSendInvitationEmail}
                  />
                </AgxColumn>
              </AgxRow>
            )}
          </>
        </Panel>
        {isExistingMember && (
          <AgentCrmRegistration
            agencyId={selectedAgency?.id ?? ''}
            agentId={teamMemberForm?.id ?? ''}
          />
        )}
      </AgxRow>

      <DeleteConfirmation
        name={`${teamMemberForm.firstName} ${teamMemberForm.lastName}`.trim()}
        title="Really delete user?"
        lineOne="Are you sure you want to delete user"
        lineTwo="This cannot be undone."
        primaryActionTitle="Permanently delete user"
        secondaryActionTitle="No, cancel"
        showPopUp={showDeleteConfirmation}
        disabled={softDeleteAgentPending}
        onClose={() => setShowDeleteConfirmation(false)}
        onConfirm={() => {
          if (softDeleteAgentPending) return;
          softDeleteAgent({
            agentId: teamMemberForm?.id,
          });
        }}
      />
      <Popup
        title=""
        isOpen={showSuccessModal}
        onClose={handleSuccessModalClose}
        actionButtons={[]}
        size={{ widthInPX: 600 }}
        centered
        actionButtonsCentered
        autoDismissMs={SUCCESS_MODAL_AUTO_DISMISS_MS}
      >
        <AgxColumn gap="spacing-xl" alignItems="alignCenter" fill>
          <Icon_CheckmarkCircleOutlineBig />
          <AgxTypography id="team-member-success-message" type="body_ui" size="medium">
            {successMessage}
          </AgxTypography>
        </AgxColumn>
      </Popup>
    </AgxColumn>
  );
};

export default AddOrEditTeamMemberPage;
